// TradeResumeTimer.jsx — live countdown to when auto-trading resumes (2026-10-11).
// Source: GET /api/trading/gate-status → resume {state, limit_type, resume_at_utc, limit_reset_at_utc}.
//   state "limit"   → daily loss/profit limit hit: big red/green pill, counts to the first tradeable
//                      minute after the 05:00 UTC (Jamaica midnight) reset
//   state "hours"   → outside trading hours: small muted pill, counts to the next window
//   state "trading" / "off" → nothing shown
// Polls every 30s and re-fetches as soon as the countdown reaches zero.
import React, { useEffect, useState, useCallback } from "react";

const jaTime = (iso) => new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/Jamaica", hour: "numeric", minute: "2-digit" });
const fmtLeft = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${h ? `${h}h ` : ""}${String(m).padStart(h ? 2 : 1, "0")}m ${String(sec).padStart(2, "0")}s`;
};

export default function TradeResumeTimer({ apiFetch }) {
  const [resume, setResume] = useState(null);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(() => {
    apiFetch("/api/trading/gate-status", { silent: true })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.resume) setResume(d.resume); })
      .catch(() => {});
  }, [apiFetch]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const target = resume?.resume_at_utc ? Date.parse(resume.resume_at_utc) : null;
  const left = target ? target - now : null;
  // Countdown finished → ask the server again (state should flip to "trading").
  const expired = left !== null && left <= 0;
  useEffect(() => { if (expired) load(); }, [expired, load]);

  if (!resume || !target || left <= 0 || (resume.state !== "limit" && resume.state !== "hours")) return null;

  const isLimit = resume.state === "limit";
  const isLoss = resume.limit_type === "loss";
  const color = isLimit ? (isLoss ? "#ef4444" : "#10b981") : "#94a3b8";
  const label = isLimit ? (isLoss ? "🛑 DAILY LOSS LIMIT HIT" : "🎯 DAILY PROFIT TARGET HIT") : "⏸ OUTSIDE TRADING HOURS";
  const resetNote = isLimit && resume.limit_reset_at_utc && resume.limit_reset_at_utc !== resume.resume_at_utc
    ? ` · limit resets ${jaTime(resume.limit_reset_at_utc)}, first trading window ${jaTime(resume.resume_at_utc)}`
    : "";
  const title = `Auto-trading resumes ${jaTime(resume.resume_at_utc)} Jamaica (${new Date(resume.resume_at_utc).toISOString().slice(11, 16)} UTC)`
    + (resume.resume_symbols?.length ? ` on ${resume.resume_symbols.join(", ")}` : "") + resetNote + (resume.note ? `\n${resume.note}` : "");

  return (
    <span className="kara-trade-resume-timer" title={title} aria-live="polite" data-state={resume.state}
      style={{ fontSize: isLimit ? 13 : 12, padding: isLimit ? "4px 10px" : "3px 8px", borderRadius: 4, fontWeight: 700,
        background: isLimit ? `${color}1f` : "#0d1f35", color, border: `1px solid ${color}${isLimit ? "aa" : "44"}`,
        fontFamily: "monospace", whiteSpace: "normal", maxWidth: "100%", display: "inline-block", animation: isLimit ? "pulse 2s infinite" : "none" }}>
      {label} · resumes in {fmtLeft(left)} <span style={{ fontWeight: 500, opacity: 0.85 }}>({jaTime(resume.resume_at_utc)} JA{resume.resume_symbols?.length ? ` · ${resume.resume_symbols.join("/")}` : ""})</span>
    </span>
  );
}
